<aside class="cart__panel" id="aria_cart" aria-labelledby="aria_cart_heading">
  <header class="sidebar-header">
    <mat-toolbar>
      <mat-icon>shopping_cart</mat-icon>
      <h2 id="aria_cart_heading" tabindex="-1" cdkFocusInitial>Cart</h2>
      <button class="close-button" type="button" matIconButton (click)="onClick_toggleCart()" aria-label="close" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </mat-toolbar>
  </header>

  <div class="partition">
    @for (partition of (cartService.cart$ | async).partitions | keyvalue; track partition; let i = $index) {
      @if (hasMultiplePartitions()) {
        <h3 class="partition__title">
          {{ getCartPartitionHeading(partition.value.items) }}
        </h3>
      }
      @if (partition.value.items.length > 0) {
        <!-- Grouped once per payment: the account numbers are only meaningful within the
             response they arrived in, so items of separate payments never share a group. -->
        @let accountGroups = getAccountGroups(partition.value.items);
        @let showAccountHeadings = hasAccountGroupHeadings(accountGroups);
        @for (group of accountGroups; track group.accountGroup) {
          @if (showAccountHeadings) {
            <h4 class="account-group__title">{{ group.label ?? 'Other items' }}</h4>
          }
          <ul class="cart__details cart__details--items" [attr.data-partition]="partition.key">
            @for (item of group.items; track item) {
              <li class="item">
                @if (!inboundRedirectService.isInboundRedirectUIContext) {
                  <button type="button"
                    class="delete-button"
                    matIconButton
                    (click)="onClick_removeFromCart(item, $event.currentTarget)"
                    [attr.aria-label]="'Remove ' + item.title + ' from cart'"
                    matTooltip="Remove From Cart">
                    <mat-icon>delete</mat-icon>
                  </button>
                }
                <div class="item__label">
                  @if (!inboundRedirectService.isInboundRedirectUIContext || inboundRedirectService.allowCartItemDetailsPage) {
                    <a
                      [routerLink]="'cart/'+item.itemGuid"
                      class="link"
                      [matTooltip]="item.title">{{item.title}}
                    </a>
                  }
                  @if (inboundRedirectService.isInboundRedirectUIContext && !inboundRedirectService.allowCartItemDetailsPage) {
                    <span [matTooltip]="item.title"
                    >{{item.title}}</span>
                  }
                </div>
                @if (item.warning && item.warning.requiresAction) {
                  <button matIconButton
                    class="caution"
                    type="button"
                    (click)="onClick_displayWarning(item)"
                    [attr.aria-label]="'View warning for ' + item.title">
                    <mat-icon>warning</mat-icon>
                  </button>
                }
                <span class="item__price">{{item.amountInCart | currency}}</span>
              </li>
            }
          </ul>
        }
      }
      @if (cartDetails.itemCount > 0) {
        <mat-divider class="partition__divider--solid"></mat-divider>
      }
      @if (cartDetails.itemCount > 0) {
        <app-cart-summary-totals
          [subtotal]="cartDetails.subtotal?.[partition.key]"
          [convenienceFee]="cartDetails.convenienceFees?.[partition.key]"
          [total]="cartDetails.total?.[partition.key]"
          [feeConfig]="getPartitionFeeConfig(partition.value.items)"
          [checkoutEnabled]="(cartService.cart$ | async)?.checkoutEnabled"
          [calculating]="cartService.calculatingTotals"
          [iconSize]="20">
        </app-cart-summary-totals>
      }
      @if (i < getPartitionsLength() - 1) {
        <mat-divider class="partition__divider--dashed" ></mat-divider>
      }
    }
  </div>

  @if (cartDetails.itemCount > 0 && disclaimer) {
    <div class="disclaimer" [ngClass]="{
      'disclaimer--checkout-page': paymentPage
    }">
      <span class="disclaimer__preview" ellipsis [ellipsis-content]="shortDisclaimer" ellipsis-word-boundaries></span>
      <button matButton type="button" (click)="onClick_showDisclaimer()" aria-controls="aria_disclaimer" aria-haspopup="dialog">
        <mat-icon>info</mat-icon>
        <span>View Full Disclaimer</span>
      </button>
    </div>
  }

  @if (cartDetails.itemCount > 0 && cartDetails.cartStatus !== 'Processing' && (!inboundRedirectService.isInboundRedirectUIContext || paymentRedirect)) {
    <div class="payment-details">
      @if (siteMetadataService.authenticationEnabled && !authService.isSignedIn && paymentRedirect) {
        <h4>Checkout as Guest</h4>
      }
      @if (!paymentPage) {
        <form [attr.role]="!authService.isSignedIn && paymentRedirect ? 'form' : 'generic'"
          #guestEmailForm="ngForm"
          class="cart-form"
          (ngSubmit)="onSubmit_proceedToPayment(guestEmailForm)">
          @if (!authService.isSignedIn && paymentRedirect) {
            <mat-form-field class="payment-details__email">
              <mat-label>Email</mat-label>
              <input matInput
                [(ngModel)]="guestDetails.email"
                autocomplete="email"
                type="email"
                name="Email"
                #emailModel="ngModel"
                required
                [pattern]="validationService.emailRegex"
                maxlength="256">
                @if (emailModel.invalid) {
                  <mat-error>{{validationService.getFieldErrorMessage(emailModel.name, emailModel, validationService.fieldErrors[emailModel.name])}}</mat-error>
                }
              </mat-form-field>
            }
            <button matButton="filled"
              type="submit"
              class="payment-details__submit-btn"
              [disabled]="!(cartService.cart$ | async).checkoutEnabled || !paymentProvider.validConfig">
              Checkout
            </button>
          </form>
        }
        @if (siteMetadataService.authenticationEnabled && !authService.isSignedIn && !inboundRedirectService.isInboundRedirectUIContext) {
          <p>Do you have an account? <a routerLink="/sign-in">Sign in</a> to save time.</p>
        }
      </div>
    }

    @if (cartDetails.itemCount > 0 && cartDetails.cartStatus === 'Processing' && !paymentPage && !inboundRedirectService.isInboundRedirectUIContext) {
      <div class="payment-details payment-details--processing">
        <p class="payment-details__disclaimer" id="aria_processing_disclaimer">It looks like you started a transaction with our payment partner.<br>
          To continue, click checkout to be taken back to pay.<br>
        To edit your cart instead, click unlock.</p>
        <button type="button" matButton="tonal"
          class="payment-details__submit-btn"
          aria-describedby="aria_processing_disclaimer"
          (click)="onClick_setCartOpen()">
          <mat-icon>lock</mat-icon>
          <span>Unlock</span>
        </button>
        <button type="button" matButton="filled"
          class="payment-details__submit-btn"
          aria-describedby="aria_processing_disclaimer"
          (click)="onClick_proceedToPayment()"
          [disabled]="!(cartService.cart$ | async).checkoutEnabled">
          <span>Checkout</span>
          <mat-icon>arrow_forward</mat-icon>
        </button>
      </div>
    }

    @if (cartDetails.itemCount === 0) {
      <div class="cart__empty-details">
        <mat-icon svgIcon="cart-empty" class="cart__empty-image"></mat-icon>
        <p>
          Your cart is empty. To pay for an item, search for it from the home page and add it to your cart.
        </p>
        <a matButton="filled" routerLink="/">Home</a>
      </div>
    }
  </aside>
